import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import {
    useNavigate,
    useLocation,
} from 'react-router-dom';
import {connect, useDispatch, useSelector} from 'react-redux';
import shortid from 'shortid';
import { ConsultaAPI } from '../http-requests';
import { Store } from '../../store';
import { toggleLoading } from '../../store/ducks/pageConfig';
import { useAppContext } from './AppContextProvider';
import {APIHelper, ConsultaHelper, StringHelper} from '../helpers';
import { changeOpcoesResultado, changeSearchOptions, limparFiltros } from '../../store/ducks/pesquisa';
import { usePrefiterParams } from '../hooks';
import eventBus from '../events/GlobalEvent';
import Axios, { CancelToken } from 'axios';
import { SearchFormType } from '../types/general';

interface SearchContextProps {
    searchTerm: string | undefined;
    changeSearchTerm: (term: string) => void;
    loadingSearch: boolean | undefined;
    changeLoadingSearch: (loadingSearch: boolean) => void;
    search: (form: SearchFormType, urlParams?: any, shouldGenerateSearchId?: boolean, cancelToken?: CancelToken) => Promise<void>;
    lastSearchForm: any;
    storeLastSearchForm?: (form: any) => void;
    makeAndRunSearch: (params: any) => void;
    researchDone: boolean;
    orderBy: string;
    changeOrderBy: (ordeCode: string) => void;
    perPage: any;
    changePerPage: (perPage: any) => void;
    getPrefilterParams: (form: any) => any;
    clearSearch: () => void;
    rep: string|null;
    changeRep: (rep:string|null)=>void
    ordenacao: any
}
const SearchContext = createContext<SearchContextProps | undefined>(undefined);
interface SearchContextProviderProps {
    children: ReactNode;
    opcoesConsultaRx?: any;
    consultaRx?: any;
    guiasPesquisaRx: any[];
    opcoesResultadoRx: any;
}
const SearchContextProvider: FC<SearchContextProviderProps> = ({children, consultaRx, guiasPesquisaRx, opcoesResultadoRx, opcoesConsultaRx}) => {

    const [searchTerm, setSearchTerm] = useState<string|undefined>()
    const [loadingSearch, setLoadingSearch] = useState<boolean>(false)
    const changeSearchTerm = useCallback((term: string) => setSearchTerm(term), [])
    const changeLoadingSearch = useCallback((loadingSearch: boolean) => setLoadingSearch(loadingSearch), [])
    const location = useLocation()
    const navigate = useNavigate();
    const [lastSearchForm, setLastSearchForm] = useState<any>();
    const { selectedTab } = useAppContext();
    const { getAll } = usePrefiterParams();
    const [ordenacao, setOrdenacao] = useState<any>()
    const [rep, setRep] = useState<string|null>(null);
    const dispatch = useDispatch();
    const [orderBy, setOrderBy] = useState<any>(undefined)
    const [perPage, setPerPage] = useState<any>(undefined)
    const targetSearchCountRef = useRef(0);

    useEffect(() => {
        setLastSearchForm(undefined);
    }, [selectedTab]);

    useEffect(() => {
        if(ordenacao && ordenacao?.length > 0){
            const firstItem = ordenacao[0].codigo;
            dispatch(changeOpcoesResultado({
                ...opcoesResultadoRx,
                ordenacaoCombo: firstItem,
                ordenacao: firstItem === 'ano_publicacao' ? 'D': 'C'
            }))
        }
    }, [ordenacao, selectedTab]);

    useEffect(() => {
        const cancelTokenSrc = Axios.CancelToken.source()
        const cachedOrdenacao = sessionStorage.getItem('ordenacao_options');
        if (cachedOrdenacao) {
            const parsedOrdenacao = JSON.parse(cachedOrdenacao);
            setOrdenacao(parsedOrdenacao);  
            const padrao = parsedOrdenacao.find((item: any) => item.padrao)?.codigo || parsedOrdenacao[0].codigo;
            dispatch(changeOpcoesResultado({
                ...opcoesResultadoRx,
                ordenacaoCombo: padrao,
                ordenacao: padrao === 'ano_publicacao' ? 'D': 'C'
            }))
        } else {
            ConsultaAPI.ordenacao(cancelTokenSrc.token)
                .then(response => {
                    const { data } = response;
                    if(data && data?.length){
                        sessionStorage.setItem('ordenacao_options', JSON.stringify(data));
                        setOrdenacao(data);
                        const firstItem = data[0].codigo;
                        dispatch(changeOpcoesResultado({
                            ...opcoesResultadoRx,
                            ordenacaoCombo: firstItem,
                            ordenacao: firstItem === 'ano_publicacao' ? 'D': 'C'
                        }))
                    }
                })
                .catch(error => {
                    if (!Axios.isCancel(error))
                        APIHelper.tratarErro(error);
                });
        }
        return () => cancelTokenSrc.cancel()
    }, []);

    /**
     * ESSE É O PRINCIPAL EFEITO RESPONSÁVEL POR DISPARAR A PESQUISA!
     * TOMAR MUITO CUIDADO AO ALTERAR AS DEPENDÊNCIAS DESTE EFEITO.
     * Toda vez que a query string da URL mudar, esse efeito é disparado.
     */
    useEffect(() => {
        const fields = getFieldsBySearchTab();
        if (guiasPesquisaRx.length === 0) return;
        if(fields?.base == 'target_gedweb')
            setResearchDone(false)

        if (fields !== null && selectedTab) {
            if (ConsultaHelper.validateSearchFields(fields, isPaginatedSearch)) {
                search(fields, getQueryParams() ?? undefined);
            }
        }

    }, [location.search, selectedTab, guiasPesquisaRx]);

    const changeRep = (rep: string|null) => setRep(rep)
    const changeOrderBy = (orderCode: string) => setOrderBy(orderCode)
    const changePerPage = (perPage: any) => setPerPage(perPage)

    const isPaginatedSearch = useMemo(
        () =>
            selectedTab !== 'pesquisa_avancada' && [null, 'local'].includes(rep) && (guiasPesquisaRx && !!guiasPesquisaRx?.find(guia => guia.codigo === selectedTab && guia.usa_consulta_paginada,)),
        [guiasPesquisaRx, selectedTab, rep],
    );

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        let rep = searchParams.get('rep');
        rep = rep ? decodeURIComponent(rep) : null;
        setRep(rep ?? null);
    }, [location.search]);

    useEffect(() => {
        if (selectedTab === 'arquivo') return;
        const searchParams = new URLSearchParams(location.search);
        const research = searchParams.get('research');
        if (research) {
            searchParams.delete('research');
            navigate({
                pathname: `/${selectedTab}`,
                search: searchParams.toString(),
            }, { replace: true });
            const fields = getFieldsBySearchTab();
            if (fields !== null) {
                if (ConsultaHelper.validateSearchFields(fields, isPaginatedSearch)) {
                    search(fields, {});
                }
            }
        }
    }, [location, selectedTab]);

    const getPrefilterParams = (currentForm: any) => {
        const queryParams = getAll();
        const { id_biblioteca, id_colecao,  tipo_obra, id, cr } = queryParams;
        let fields: any = {};

        if (id_biblioteca && !currentForm.biblioteca)
            fields.biblioteca = id_biblioteca;
        if (cr && !currentForm.bib_virtual)
            fields.bib_virtual = cr;
        if (id_colecao && !currentForm.colecao)
            fields.colecao = id_colecao;
        if (tipo_obra && !currentForm.cod_tipo_obra)
            fields.cod_tipo_obra = tipo_obra;
        if (id && !currentForm.id_campus)
            fields.id_campus = id;
        return fields;
    }

    const search = useCallback(
        async (
            form: SearchFormType,
            urlParams?: any,
            shouldGenerateSearchId = true,
            cancelToken?: CancelToken
        ): Promise<void> => {
            if (loadingSearch) return;
            const isTargetSearch =
                (form?.base === 'target_gedweb' || urlParams?.rep === 'target_gedweb') &&
                !!form?.coluna_um;
            const { ordenacao: direction, ordenacaoCombo: orderBy} = opcoesResultadoRx
            dispatch(limparFiltros())
            if (isPaginatedSearch) {
                if(!form.orderBy){
                    form.orderBy = orderBy;
                    urlParams.orderBy = orderBy;
                }
                if(!form.direction){
                    form.direction = direction;
                    urlParams.direction = direction;
                }
            }
            setResearchDone(true);
            setLoadingSearch(true);
            /**
             * Preenche o state legado
             */
            fillLegacyState(form);

            form.search_id =
                !shouldGenerateSearchId && consultaRx
                    ? consultaRx.id
                    : shortid.generate();
            if (!form.search_tab) form.search_tab = selectedTab;

            if (
                selectedTab === 'pesquisa_geral' &&
                urlParams?.rep === 'rede_pergamum_oai'
            ) {
                setLastSearchForm({
                    ...form,
                    coluna_um: urlParams.for,
                });
            } else {
                setLastSearchForm(form);
            }

            const searchParams = new URLSearchParams(location.search);

            // Remover filtros anteriores da URL ao realizar nova pesquisa
            Array.from(searchParams.keys()).forEach(key => {
                if (key.startsWith('f') && key.length === 2) {
                    searchParams.delete(key);
                }
            });

            if (urlParams) {
                Object.keys(urlParams).forEach((key: string) => {
                    const value = urlParams[key];
                    if(value){
                        searchParams.set(key, encodeURIComponent(value));
                    }
                });
            }

            const repInUrl = searchParams.get('rep');
            if (repInUrl && !urlParams?.rep) {
                searchParams.delete('rep');
            }

            const repBases = searchParams.get('repBases');
            if (repBases && !urlParams?.repBases) {
                searchParams.delete('repBases');
            }

            navigate({
                pathname: `/${form.search_tab}`,
                search: searchParams.toString(),
            }, { replace: true });

        const fullUrl = `/${form.search_tab}?${searchParams.toString()}`;
        form = {...form, ...getPrefilterParams(form)}
        return new Promise((resolve, reject): void => {
            try{
                ConsultaAPI.buscarConsulta(
                    form.search_tab,
                    form,
                    fullUrl,
                    () => {

                        if(selectedTab === 'pesquisa_avancada') {
                            if(form.ordenacao){
                                changeOrderBy(form.ordenacao)
                            }
                            if(form.perPage){
                                changePerPage(form.perPage)
                                dispatch(
                                changeOpcoesResultado({
                                    ...opcoesResultadoRx,
                                    quantPag: parseInt(form.perPage, 10)
                                }))
                            }
                        }

                        Store.dispatch(toggleLoading(false));
                        setLoadingSearch(false);

                        if (isTargetSearch) {
                            targetSearchCountRef.current += 1;

                            if (targetSearchCountRef.current === 2) {
                                setTimeout(() => {
                                    window.location.reload();
                                }, 200);
                                resolve();
                                return;
                            }
                        }
                        
                        // Scroll automático para os resultados da pesquisa
                        setTimeout(() => {
                            const consultaContent = document.querySelector('.consultaContent');
                            if (consultaContent) {
                                consultaContent.scrollIntoView({
                                    block: 'start',
                                    inline: 'nearest',
                                    behavior: 'smooth',
                                });
                            }
                        }, 300);
                            resolve();
                        },
                        isPaginatedSearch,
                        //@ts-ignore
                        cancelToken
                    );
                } catch (error) {
                    reject(error);
                }
            });
        },
        [history, selectedTab, loadingSearch, isPaginatedSearch, opcoesResultadoRx, location],
    );
    const [researchDone, setResearchDone] = useState(true);

    const getQueryParams = (): any => {
        const params = new URLSearchParams(location.search);
        const queryParams: any = {};

        params.forEach((value, key) => {
            queryParams[key] = decodeURIComponent(value);
        });

        return queryParams;
    };

    const fillLegacyState = (form: SearchFormType): void => {
        const opcoesConsulta = [];
        switch (selectedTab) {
            case 'pesquisa_geral':
                opcoesConsulta.push({
                    option: form.coluna_um || 'LIVRE',
                    termo: form.termo_pesquisa,
                });
                break;
            case 'e_books':
            case 'autoridades':
            case 'acessibilidade':
                opcoesConsulta.push({
                    option: form.coluna_um,
                    termo: form.termo_pesquisa,
                });
                break;
            case 'arquivo':
            case 'pesquisa_avancada':
            case 'atos_normativos':
            case 'jurisprudencia':
                if (form.termo_pesquisa && form.coluna_um) {
                    const linha1 = {
                        option: form.coluna_um,
                        termo: form.termo_pesquisa,
                        condition: form.condicao_um,
                    };
                    opcoesConsulta.push(linha1);
                }

                if (
                    form.condicao_um &&
                    form.termo_pesquisa_dois &&
                    form.coluna_dois
                ) {
                    const linha2 = {
                        option: form.coluna_dois,
                        termo: form.termo_pesquisa_dois,
                        condition: form.condicao_dois,
                    };
                    opcoesConsulta.push(linha2);
                }

                if (
                    form.condicao_dois &&
                    form.termo_pesquisa_tres &&
                    form.coluna_tres
                ) {
                    const linha3 = {
                        option: form.coluna_tres,
                        termo: form.termo_pesquisa_tres,
                    };
                    opcoesConsulta.push(linha3);
                }
                break;
            case 'novas_aquisicoes':
                opcoesConsulta.push({
                    option: form.coluna_um,
                    termo: form.termo_pesquisa,
                });
                break;
            case 'plano_ensino':
                break;
            case 'multimeios':
                opcoesConsulta.push({
                    option: form.coluna_um,
                    termo: form.termo_pesquisa,
                });
                break;
            default:
                opcoesConsulta.push({
                    option: form.coluna_um,
                    termo: form.termo_pesquisa,
                });
                break;
        }
        dispatch(changeSearchOptions(opcoesConsulta));
    };

    const getFieldsBySearchTab = (params?: any): SearchFormType | null => {
        const queryParams: any =
            params !== undefined ? params : getQueryParams();

        if (Object.keys(queryParams).length === 0) {
            return null;
        }

        const { rep } = queryParams;

        let fields: any = null;

        const commonSearch = [
            'pesquisa_geral',
            'e_books',
            'autoridades',
            'acessibilidade',
            'novas_aquisicoes',
            'multimeios'
        ];

        const otherSearch = [
            'arquivo',
            'pesquisa_avancada',
            'atos_normativos',
            'jurisprudencia',
        ];

        if (commonSearch.includes(selectedTab)) {
            const {
                tipo_obra_total,
                for: forQuery,
                aut_id,
                index,
            } = queryParams;

            if (tipo_obra_total) {
                fields = {
                    termo_pesquisa: 'tipo_obra_total',
                    coluna_um: forQuery,
                    autoridade: aut_id,
                    indice: index,
                    tipo_obra_total: StringHelper.getWithCommaAtTheEnd(tipo_obra_total),
                };
            } else {
                const { q, tipo_obra } = queryParams;
                fields = {
                    termo_pesquisa: q,
                    coluna_um: forQuery,
                    autoridade: aut_id,
                    indice: index,
                };

                // A spwper_consultas_ind espera o tipo de obra em tipo_obra (com vírgula ao final)
                if (tipo_obra && index) {
                    fields.tipo_obra = tipo_obra;
                }
            }

            if (selectedTab === 'pesquisa_geral') {
                const { rep, repBases } = queryParams;
                if (rep) {
                    fields.base = rep;
                }
                if (repBases) {
                    fields.coluna_um = `${forQuery},${repBases}`;
                }
            }
        } else if (otherSearch.includes(selectedTab)) {
            const {
                q,
                for: forQuery,
                for2,
                q2,
                condition,
                for3,
                q3,
                condition2,
            } = queryParams;

            fields = {
                termo_pesquisa: q,
                coluna_um: forQuery,
            };

            if (for2 || q2 || condition) {
                fields = {
                    ...fields,
                    termo_pesquisa_dois: q2,
                    coluna_dois: for2,
                    condicao_um: condition,
                };
            }

            if (for3 || q3 || condition2) {
                fields = {
                    ...fields,
                    termo_pesquisa_tres: q3,
                    coluna_tres: for3,
                    condicao_dois: condition2,
                };
            }

            if (selectedTab === 'pesquisa_avancada') {
                const {
                    cr,
                    keyword_type,
                    local,
                    idioma,
                    ano_publicacao,
                    ano_publicacao_final,
                    comp_ano_publicacao,
                    categoria,
                    ordenacao,
                    perPage,
                    local_interna
                } = queryParams;

                if (cr) {
                    fields = {
                        ...fields,
                        bib_virtual: cr.toUpperCase() === 'S'
                            ? cr.toUpperCase()
                            : 'N',
                    };
                }

                if (local) {
                    fields = {
                        ...fields,
                        local,
                    };
                }

                if (idioma) {
                    fields = {
                        ...fields,
                        idioma,
                    };
                }

                if (ano_publicacao) {
                    fields = {
                        ...fields,
                        ano_publicacao,
                    };
                }

                if (comp_ano_publicacao) {
                    fields = {
                        ...fields,
                        comp_ano_publicacao,
                    };
                }

                if(ano_publicacao_final){
                    fields = {
                        ...fields,
                        ano_publicacao_final,
                    };
                }

                if(categoria){
                    fields = {
                        ...fields,
                        categoria
                    }
                }

                if(ordenacao){
                    fields = {
                        ...fields,
                        ordenacao
                    }
                }

                if(perPage){
                    fields = {
                        ...fields,
                        perPage
                    }
                }

                if(perPage){
                    fields = {
                        ...fields,
                        perPage
                    }
                }

                if(local_interna){
                    fields = {
                        ...fields,
                        local_interna
                    }
                }
                fields = { ...fields, tipo_termos: keyword_type };
            }
        } else if (selectedTab === 'plano_ensino') {
            const { curso, disciplina, campus, biblioteca, professor } =
                queryParams;

            fields = {
                tipo_termos: 99,
            };

            if (campus) {
                fields.cod_campus_selecao = campus;
            }

            if (biblioteca) {
                fields.biblioteca = biblioteca;
            }

            const items = [
                { coluna: 'CURSO', termo: curso },
                { coluna: 'DISCIPLINA', termo: disciplina },
                { coluna: 'PROFESSOR', termo: professor },
            ].filter(item => typeof item.termo !== 'undefined');

            const assoc = ['um', 'dois', 'tres'];

            items.forEach((item, index): void => {
                // @ts-ignore
                fields[`coluna_${assoc[index]}`] = item.coluna;
                const stringEnd = index === 0 ? '' : `_${assoc[index]}`;
                // @ts-ignore
                fields[`termo_pesquisa${stringEnd}`] = item.termo;
                // @ts-ignore
                fields[`condicao_${assoc[index]}`] = 'AND';
            });
        }

        // Parametros de paginacao
        if (isPaginatedSearch) {
            const { page, perPage, orderBy, direction } = queryParams;
            fields = {
                ...fields,
                page,
                perPage,
                orderBy,
                direction,
            };

            // if(!fields.orderBy){
            //     fields.orderBy = process.env.REACT_APP_CONSULTA_PAGINADA_ORDENACAO ?? process.env.ORDENACAO;
            // }

            for (const [key, value] of Object.entries(queryParams)) {
                if (key.startsWith('f') && key.length === 2) {
                    fields[key] = value;
                }
            }
        }

        if (fields !== null) {
            fields = {
                ...fields,
                search_tab: selectedTab,
                base: rep !== 'local' ? rep : '',
            };
        }
        return fields;
    };
    const makeAndRunSearch = (params: any) => {
        const fields = getFieldsBySearchTab(params);

        if (fields !== null) {

            if(isPaginatedSearch){
                fields.page = 1;
                fields.perPage = 20;
                // fields.orderBy =  process.env.REACT_APP_CONSULTA_PAGINADA_ORDENACAO || process.env.ORDENACAO
                //fields.direction = 'C'
            }

            if (ConsultaHelper.validateSearchFields(fields, isPaginatedSearch)) {
                search(fields, params);
            }
        }
    };

    // allow other components to store a lastSearchForm without running a search
    const storeLastSearchForm = (form: any) => {
        try {
            setLastSearchForm(form);
        } catch (e) {
            // ignore
        }
    };

    const clearSearch = () => {
        eventBus.emit("CLEAR_SEARCH")
        clearQueryParams()
    }

    const clearQueryParams = () => {
        window.history.replaceState(null, "", window.location.pathname);
    };

    const memoizedData: SearchContextProps = useMemo(() => ({
        changeSearchTerm,
        searchTerm,
        loadingSearch,
        changeLoadingSearch,
        search,
        lastSearchForm,
        storeLastSearchForm,
        makeAndRunSearch,
        researchDone,
        orderBy,
        changeOrderBy,
        perPage,
        changePerPage,
        getPrefilterParams,
        clearSearch,
        eventBus,
        rep,
        changeRep,
        ordenacao
    }), [
        searchTerm,
        loadingSearch,
        lastSearchForm,
        selectedTab,
        opcoesConsultaRx,
        researchDone,
        consultaRx,
        rep,
        orderBy,
        ordenacao,
        opcoesResultadoRx
    ])
    return (
        <SearchContext.Provider value={memoizedData}>
            {children}
        </SearchContext.Provider>
    );
};

export const useSearchContext = (): SearchContextProps => {
    const context = useContext(SearchContext) as SearchContextProps;
    if (context === undefined)
        throw new Error(
            'useSearchContext must be used within a SearchContextProvider',
        );
    return context;
};
const mapStateToProps = (state: any) => ({
    statusRx: state.pageConfig.status,
    guiasPesquisaRx: state.pageConfig.guiasPesquisa,
    consultaRx: state.pesquisa.consulta,
    opcoesResultadoRx: state.pesquisa.opcoesResultado,
    opcoesConsultaRx: state.pesquisa.opcoesConsulta,
});

// @ts-ignore
export default connect(mapStateToProps)(SearchContextProvider);
